'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { type BlockedRecoveryGuard, recordReloadRecovery, reportBlockedRecovery } from 'lib/telemetry/autoRecovery';
import { registerBlankReason } from 'lib/telemetry/blankReasons';
import * as datadogRum from 'lib/telemetry/datadogRum';
import { reloadPage } from 'utils/reloadPage';
import {
    getLoadedBuildId,
    hasReloadedForLoadedBuild,
    isVersionSkewError,
    persistVersionSkewReloadGuard,
} from 'utils/versionSkew';

interface ChunkSkewErrorBoundaryProps {
    children: ReactNode;
    /**
     * Rendered when a skew error is caught but this build already triggered an
     * auto-reload (so we don't reload again). Defaults to null.
     */
    fallback?: ReactNode;
}

interface ChunkSkewErrorBoundaryState {
    error: Error | null;
}

/**
 * Error boundary for lazily-loaded chunks that recovers from stale-bundle
 * version skew by reloading the page, mirroring the ChunkLoadError path in
 * `utils/importWithRetry`. To avoid a reload loop it reloads at most once per
 * loaded build id per tab session; a further skew error thrown by that same
 * stale shell renders `fallback` instead. Because the guard records the build
 * that failed, a tab that survives a later deploy can still recover from the
 * new skew. If the guard can't be persisted at all it renders `fallback` rather
 * than risk an unbounded reload loop.
 *
 * Non-skew errors — including ordinary `is not a function` render bugs that
 * don't match the stale-export call shape — are rethrown during render so they
 * propagate to the nearest parent boundary unchanged.
 */
export class ChunkSkewErrorBoundary extends Component<ChunkSkewErrorBoundaryProps, ChunkSkewErrorBoundaryState> {
    private unregisterBlankReason: (() => void) | null = null;

    constructor(props: ChunkSkewErrorBoundaryProps) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error: Error): ChunkSkewErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        if (!isVersionSkewError(error)) {
            // render() rethrows so the nearest parent boundary handles it.
            return;
        }

        // Scoped to the build that failed: a reload that lands on a fresh build
        // no longer matches the stored id, so a skew from a later deploy in the
        // same tab can still recover.
        const alreadyReloaded = hasReloadedForLoadedBuild();

        try {
            datadogRum.addError(error, {
                source: 'chunk-skew-error-boundary',
                componentStack: errorInfo.componentStack ?? undefined,
                alreadyReloaded,
                loadedBuildId: getLoadedBuildId(),
            });
        } catch {
            // no-op: reporting should never crash the error boundary
        }

        // addError above is the RUM report. Console errors are captured too,
        // so keep fallback diagnostics as warnings to avoid reporting twice.
        if (alreadyReloaded) {
            console.warn(
                '[ChunkSkewErrorBoundary] Version-skew error persisted after reload - rendering fallback',
                error,
            );
            this.markTerminalFallback('already_reloaded_build');
            return;
        }

        if (!persistVersionSkewReloadGuard()) {
            console.warn(
                '[ChunkSkewErrorBoundary] Version-skew reload guard could not be persisted - rendering fallback instead of reloading',
                error,
            );
            this.markTerminalFallback('storage_unavailable');
            return;
        }

        console.warn('[ChunkSkewErrorBoundary] Version-skew error detected - reloading page', error);
        recordReloadRecovery('version_skew', 'chunk_skew_boundary', error);
        reloadPage();
    }

    componentWillUnmount(): void {
        this.unregisterBlankReason?.();
    }

    /**
     * The fallback defaults to `null` and nothing retries from here, so a
     * blank-page report should name this boundary as the cause.
     */
    private markTerminalFallback(guard: BlockedRecoveryGuard): void {
        reportBlockedRecovery('version_skew', 'chunk_skew_boundary', guard);
        this.unregisterBlankReason?.();
        this.unregisterBlankReason = registerBlankReason(`chunk-skew-fallback:${guard}`);
    }

    render(): ReactNode {
        const { error } = this.state;
        if (error !== null) {
            if (!isVersionSkewError(error)) {
                throw error;
            }
            return this.props.fallback ?? null;
        }

        return this.props.children;
    }
}

export default ChunkSkewErrorBoundary;
